import { useEffect, useRef, useState } from 'react'

/**
 * A contagem até a próxima coleta do Oracle, para a barra do rodapé.
 *
 * O intervalo é **medido**, não configurado: guarda-se a diferença entre os dois
 * últimos `coletadoEm` que chegaram. A razão é que esse número não vive no
 * painel — ele é `WR_INTERVALO_CNF_SEG` no cofre do coletor, e já mudou duas
 * vezes em 13/10 (300 s → 120 s). Uma constante aqui ficaria mentindo no
 * primeiro ajuste de cadência, e a barra do telão mentindo é pior que barra
 * nenhuma: a sala passa a achar que a coleta travou.
 *
 * O Linx fica de fora de propósito (ele carrega de 20 em 20 min): a barra é a do
 * Oracle, que é quem move o número da venda.
 *
 * @param coletadoEm  o ISO do último snapshot, como o painel o entrega
 * @returns `progresso` de 0 a 100 e `intervaloSeg`, o ciclo medido
 */
export function usarCicloDaColeta(coletadoEm: string | null): {
  progresso: number
  intervaloSeg: number
} {
  /** Ciclo observado. 120 s é a cadência de 13/10, e serve até o segundo
   *  snapshot chegar — a partir dele o valor é medido. */
  const [intervaloSeg, setIntervaloSeg] = useState(120)
  const [progresso, setProgresso] = useState(0)
  const anterior = useRef<number | null>(null)

  useEffect(() => {
    const instante = coletadoEm === null ? null : Date.parse(coletadoEm)
    if (instante === null || Number.isNaN(instante)) return
    if (anterior.current !== null) {
      const medido = Math.round((instante - anterior.current) / 1000)
      /* Piso de 20 s e teto de 900 s: retentativa do coletor pode mandar dois
         snapshots em segundos, e um processo reiniciado pode deixar um buraco de
         minutos. Nem um nem outro é o ciclo, e deixá-los entrar faria a barra
         correr em dois segundos ou ficar parada por um quarto de hora. */
      if (medido >= 20 && medido <= 900) setIntervaloSeg(medido)
    }
    anterior.current = instante
    setProgresso(0)
  }, [coletadoEm])

  useEffect(() => {
    /* UM temporizador de 1 s, como o resto do painel: o telão fica dias ligado,
       e animação por `requestAnimationFrame` aqui rodaria 60 vezes por segundo
       para mover uma barra de 4 px. A transição de CSS cobre o entre-passos. */
    const laco = setInterval(() => {
      const base = anterior.current
      if (base === null) return
      const decorrido = (Date.now() - base) / 1000
      const quanto = Math.min(Math.max((decorrido / intervaloSeg) * 100, 0), 100)
      setProgresso((antes) => (Math.abs(antes - quanto) < 0.4 ? antes : quanto))
    }, 1000)
    return () => clearInterval(laco)
  }, [intervaloSeg])

  return { progresso, intervaloSeg }
}
